import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, FileText, History, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { DateRangeSelector } from '../../features/statistics/components/DateRangeSelector';
import { MetricButton, METRICS } from '../../features/statistics/components/MetricButton';
import { useAccountDailyInsights } from '../../features/statistics/hooks/useAccountDailyInsights';
import { useDateRangeParams } from '../../features/statistics/hooks/useDateRangeParams';
import { ExportMenu } from '../../features/statistics/components/ExportMenu';
import {
  ACCOUNT_REPORTS,
  accountHistoryUrl,
  accountReportUrl,
  backToAccountsUrl,
} from '../../features/statistics/lib/account-links';
import { resolveDateRange } from '../../features/statistics/lib/date-range';
import { computeEr } from '../../features/statistics/lib/er';
import { formatCompactNumber } from '../../features/statistics/lib/format-number';
import { AudienceDemographicsSection } from '../../features/statistics/components/AudienceDemographicsSection';
import { TikTokAccountAnalytics } from './TikTokAccountAnalytics';

import type { MetricConfig, MetricKey } from '../../features/statistics/components/MetricButton';
import type { DayDataPoint } from '../../features/statistics/hooks/useAccountDailyInsights';

function formatDateTick(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatMetricValue(value: number, isPercent: boolean): string {
  if (isPercent) return `${value.toFixed(2)}%`;
  return value.toLocaleString('ru-RU');
}

type ChartTooltipProps = TooltipProps<number, string> & {
  activeMetric: MetricConfig;
};

function ChartTooltip({
  active,
  payload,
  label,
  activeMetric,
}: ChartTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) return null;

  const value = payload[0]?.value as number | null | undefined;

  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-xl text-xs min-w-[140px]">
      <div className="font-medium mb-1 text-foreground">{formatFullDate(label)}</div>
      <div className="tabular-nums text-primary font-semibold text-base">
        {value == null ? '—' : formatMetricValue(value, activeMetric.isPercent)}
      </div>
    </div>
  );
}

function ContentSkeleton(): JSX.Element {
  return (
    <>
      <div className="flex gap-2 overflow-x-auto">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 flex-1 min-w-[100px] rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-80 w-full rounded-xl" />
    </>
  );
}

// Dispatcher: TikTok accounts (id "tt:<openId>") get a dedicated detail view;
// Instagram accounts keep the daily-insights view. Separate component types so
// switching between them remounts cleanly (no hook-order violations).
export function AccountAnalyticsPage(): JSX.Element {
  const { accountId } = useParams<{ accountId: string }>();
  if (accountId?.startsWith('tt:')) {
    return <TikTokAccountAnalytics openId={accountId.slice(3)} />;
  }
  return <InstagramAccountAnalytics />;
}

function InstagramAccountAnalytics(): JSX.Element {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [dateRangeOption, setDateRangeOption] = useDateRangeParams();
  const [activeMetric, setActiveMetric] = useState<MetricKey>('impressions');
  const [comparePrev, setComparePrev] = useState(false);

  const resolvedRange = useMemo(() => resolveDateRange(dateRangeOption), [dateRangeOption]);

  const { days, totals, prevTotals, username, name, avatarUrl, isLoading, isFetching, error } =
    useAccountDailyInsights(accountId ?? '', resolvedRange, comparePrev);

  const totalEr = computeEr(totals?.impressions ?? null, totals?.interactions ?? null);
  const prevEr = computeEr(prevTotals?.impressions ?? null, prevTotals?.interactions ?? null);

  function computeDelta(current: number | null, prev: number | null): number | null {
    if (!comparePrev || current === null || prev === null) return null;
    return current - prev;
  }

  const metricTotals: Record<MetricKey, number | null> = {
    impressions: totals?.impressions ?? null,
    reach: totals?.reach ?? null,
    interactions: totals?.interactions ?? null,
    follower_delta: totals?.follower_delta ?? null,
    er: totalEr,
  };

  const metricDeltas: Record<MetricKey, number | null> = {
    impressions: computeDelta(totals?.impressions ?? null, prevTotals?.impressions ?? null),
    reach: computeDelta(totals?.reach ?? null, prevTotals?.reach ?? null),
    interactions: computeDelta(totals?.interactions ?? null, prevTotals?.interactions ?? null),
    follower_delta: computeDelta(
      totals?.follower_delta ?? null,
      prevTotals?.follower_delta ?? null
    ),
    er: computeDelta(totalEr, prevEr),
  };

  const activeConfig = METRICS.find((m: MetricConfig) => m.key === activeMetric)!;

  const chartData = useMemo(() => {
    const fromStr = `${resolvedRange.from.getFullYear()}-${String(resolvedRange.from.getMonth() + 1).padStart(2, '0')}-${String(resolvedRange.from.getDate()).padStart(2, '0')}`;
    return days
      .filter((day: DayDataPoint) => day.date >= fromStr)
      .map((day: DayDataPoint) => ({
        date: day.date,
        value:
          activeMetric === 'er' ? computeEr(day.impressions, day.interactions) : day[activeMetric],
      }));
  }, [days, activeMetric, resolvedRange.from]);

  const initials = username ? username.slice(0, 2).toUpperCase() : '??';

  function handleBack(): void {
    navigate(backToAccountsUrl(search));
  }

  return (
    <div className="flex flex-col overflow-auto h-full pb-6">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-1" onClick={handleBack}>
          <ChevronLeft className="size-4" />
          По аккаунтам
        </Button>
      </div>

      <div className="flex flex-col gap-5 px-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            {username ? (
              <>
                <Avatar className="size-10 shrink-0">
                  <AvatarImage src={avatarUrl ?? undefined} alt={username} />
                  <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold text-base leading-tight">@{username}</div>
                  {name && (
                    <div className="text-sm text-muted-foreground leading-tight">{name}</div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Skeleton className="size-10 rounded-full shrink-0" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" className="gap-1.5" asChild>
              <Link to={accountHistoryUrl(accountId ?? '', dateRangeOption)}>
                <History className="size-4" />
                История сборов
              </Link>
            </Button>
            <ExportMenu
              label="Выгрузить отчёт"
              variant="outline"
              size="sm"
              formats={ACCOUNT_REPORTS.map((r) => ({ ...r, icon: FileText }))}
              onSelect={(report) => navigate(accountReportUrl(report, accountId ?? '', dateRangeOption))}
            />
            <Button
              variant={comparePrev ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={() => setComparePrev((v: boolean) => !v)}
            >
              <TrendingUp className="size-4" />
              Динамика
            </Button>
            <DateRangeSelector value={dateRangeOption} onChange={setDateRangeOption} />
          </div>
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center py-20">
            <p className="text-muted-foreground text-sm">Не удалось загрузить данные</p>
            <p className="text-xs text-muted-foreground/70">{error.message}</p>
          </div>
        ) : isLoading ? (
          <ContentSkeleton />
        ) : (
          <>
            <div className="flex gap-2 overflow-x-auto">
              {METRICS.map((config: MetricConfig) => (
                <MetricButton
                  key={config.key}
                  config={config}
                  total={metricTotals[config.key]}
                  delta={metricDeltas[config.key]}
                  isDeltaLoading={comparePrev && isFetching}
                  isActive={activeMetric === config.key}
                  onClick={() => setActiveMetric(config.key)}
                />
              ))}
            </div>

            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-80 rounded-xl border bg-muted/20">
                <p className="text-muted-foreground text-sm">Нет данных за выбранный период</p>
              </div>
            ) : chartData.every((p: { value: number | null }) => p.value === null) ? (
              <div className="flex flex-col items-center justify-center h-80 rounded-xl border bg-muted/20 gap-1.5">
                <p className="text-muted-foreground text-sm">
                  Instagram не предоставляет дневную разбивку для этой метрики
                </p>
                <p className="text-xs text-muted-foreground/60">
                  Доступны: Охваты, Взаимодействия, Подписчики, ER
                </p>
              </div>
            ) : (
              <div className="rounded-xl border bg-card p-4">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="metricGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateTick}
                      tick={{
                        fontSize: 11,
                        fill: 'hsl(var(--muted-foreground))',
                      }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(v: number) =>
                        activeConfig.isPercent ? `${v.toFixed(1)}%` : formatCompactNumber(v)
                      }
                      tick={{
                        fontSize: 11,
                        fill: 'hsl(var(--muted-foreground))',
                      }}
                      tickLine={false}
                      axisLine={false}
                      width={52}
                    />
                    <Tooltip
                      content={props => <ChartTooltip {...props} activeMetric={activeConfig} />}
                      cursor={{
                        stroke: '#2563eb',
                        strokeWidth: 1,
                        strokeDasharray: '4 4',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="var(--chart-1)"
                      strokeWidth={2.5}
                      fill="url(#metricGradient)"
                      dot={false}
                      activeDot={{
                        r: 5,
                        fill: '#2563eb',
                        strokeWidth: 2,
                        stroke: '#fff',
                      }}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}

        <AudienceDemographicsSection
          accountId={accountId ?? ''}
          title="Охваченная аудитория"
          metric="reached_audience_demographics"
        />
        <AudienceDemographicsSection
          accountId={accountId ?? ''}
          title="Подписчики"
          metric="follower_demographics"
        />
      </div>
    </div>
  );
}
